import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { PageHeader, KpiCard, Card, Spinner, AIValueLabel, DecisionBadge } from "@/components/shared";
import { EvidenceQualityCard } from "@/components/app/EvidenceQualityCard";
import { money, hours, decisionV2 } from "@/lib/format";
import api, { track } from "@/lib/api";
import { toast } from "sonner";
import { Info, RotateCcw, ArrowRight, ShieldCheck, Scale, Wallet, TrendingUp, Clock, Cpu } from "lucide-react";

const NEXT_ACTION = {
  expand: "Expand where AI is proven.", scale: "Expand where AI is proven.",
  continue: "Continue — AI is working.", expand_pilot: "Gather more evidence before expanding.",
  review: "Review before deciding.", monitor: "Review before deciding.", needs_more_evidence: "Add evidence before deciding.",
  improve: "Improve usage, process, or tool choice.", review_process: "Improve usage, process, or tool choice.",
  stop: "Stop or rework — negative return.", pause: "Stop or rework — negative return.", negative_value: "Stop — cost exceeds value.",
};

const TONE_CLS = { pos: "text-[#1E9E6A]", neg: "text-coral", cost: "text-amber-700", ink: "text-navy" };

function FlowStat({ icon: Icon, label, value, sub, tone = "ink", strong }) {
  return (
    <div className={`rounded-lg border p-4 ${strong ? "border-navy/30 bg-navy/[0.03]" : "border-slate-200 bg-white"}`}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </p>
      <p className={`kpi-number mt-1.5 text-2xl font-semibold ${TONE_CLS[tone]} ${strong ? "lg:text-[28px]" : ""}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

function FlowOp({ symbol }) {
  return <div className="hidden items-center justify-center lg:flex"><span className="text-2xl font-light text-slate-300">{symbol}</span></div>;
}

export default function DemoDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const cur = "USD";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/demo/executive");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    track("demo_dashboard_viewed");
    load();
  }, []);

  const reset = async () => {
    setResetting(true);
    try {
      await api.post("/demo/reset");
      await load();
      toast.success("Sample data reset.");
    } finally {
      setResetting(false);
    }
  };

  const k = data?.kpis;
  const view = data?.can_view_costs;
  const eq = k?.evidence_quality;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="border-b border-amber-200 bg-amber-50">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <p className="flex items-center gap-2 text-sm text-amber-800">
            <Info className="h-4 w-4" />
            You are viewing sample data. Create your own workspace to track your real AI ROI.
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={reset} disabled={resetting} data-testid="demo-reset-btn">
              <RotateCcw className="mr-2 h-3.5 w-3.5" /> Reset sample data
            </Button>
            <Button size="sm" className="bg-editorial hover:bg-editorial/90" onClick={() => navigate("/signup")} data-testid="demo-signup-btn">
              Start Free Trial <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        {loading || !k ? (
          <Spinner />
        ) : (
          <>
            <PageHeader eyebrow="Dashboard" title="Dashboard" subtitle="Compare time with and without AI to see what AI saves, costs, and returns across your business." testid="demo-dashboard-header" />

            {view && (
              <AIValueLabel
                className="mb-6"
                title="AI Value Label"
                verified={eq.band === "value_verified"}
                status={data.decision_status}
                nextAction={NEXT_ACTION[data.decision_status]}
                fields={[
                  { label: "Time Saved", value: hours(k.net_hours_saved), tone: (k.net_hours_saved || 0) >= 0 ? "positive" : "negative" },
                  { label: "Current Net Value", value: money(k.current_net_value, cur), tone: (k.current_net_value || 0) >= 0 ? "positive" : "negative" },
                  { label: "Future Value", value: money(k.forecast_future_value, cur), tone: "ink" },
                  { label: "AI Cost", value: money(k.ai_cost, cur), tone: "ink" },
                  { label: "Evidence Quality", value: eq.score == null ? "—" : `${eq.band_label}`, tone: "editorial" },
                  { label: "Next Action", value: decisionV2(data.decision_status).label, tone: "ink" },
                ]}
                testid="lift-value-label"
              />
            )}

            {view && (
              <Card className="mb-6" testid="value-story">
                <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1.1fr]">
                  <FlowStat icon={Clock} label="Staff time value" value={money(k.staff_cost_impact, cur)} tone={(k.staff_cost_impact || 0) >= 0 ? "pos" : "neg"} sub={`${hours(k.net_hours_saved)} saved`} />
                  <FlowOp symbol="−" />
                  <FlowStat icon={Cpu} label="AI cost" value={money(k.ai_cost, cur)} tone="cost" sub="software, usage, training" />
                  <FlowOp symbol="=" />
                  <FlowStat label="Current net value" value={money(k.current_net_value, cur)} tone={(k.current_net_value || 0) >= 0 ? "pos" : "neg"} strong sub="time value minus AI cost" />
                </div>
                <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500"><Info className="h-3.5 w-3.5" /> Current net value blends confirmed P&amp;L impact and estimated capacity value — shown separately below.</p>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="value-kpis">
              <KpiCard label="Time saved / lost" value={hours(k.net_hours_saved)} accent={k.net_hours_saved >= 0 ? "positive" : "negative"} sub={`${hours(k.ai_assisted_hours)} AI-assisted`} testid="kpi-net-hours" />
              <KpiCard label="Current net value" value={money(k.current_net_value, cur)} accent={(k.current_net_value || 0) >= 0 ? "positive" : "negative"} testid="kpi-current-net" />
              <KpiCard label="Future value" value={money(k.forecast_future_value, cur)} accent="navy" sub="forecast" testid="kpi-forecast-future" />
              <KpiCard label="Evidence quality" value={eq.score == null ? "—" : eq.band_label} accent="navy" sub="how reliable this is" testid="kpi-evidence" />
            </div>

            {view && (
              <div className="mt-4 grid gap-4 sm:grid-cols-3" data-testid="pnl-capacity-split">
                <div className="rounded-lg border border-green-200 bg-green-50/40 p-5">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-green-700"><Wallet className="h-3.5 w-3.5" /> Actual P&amp;L impact</p>
                  <p className="kpi-number mt-2 text-3xl font-semibold text-green-700">{money(k.actual_pnl_impact, cur)}</p>
                  <p className="mt-1 text-xs text-slate-500">Confirmed financial impact that affects profit and loss — reduced spend, avoided cost, or directly attributable revenue, net of AI spend.</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-5">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500"><Scale className="h-3.5 w-3.5" /> Estimated capacity value</p>
                  <p className="kpi-number mt-2 text-3xl font-semibold text-slate-900">{money(k.estimated_capacity_value, cur)}</p>
                  <p className="mt-1 text-xs text-slate-500">Estimated value of time saved when the team has more capacity, even if cash spend has not reduced.</p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-5">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-700"><Cpu className="h-3.5 w-3.5" /> AI cost</p>
                  <p className="kpi-number mt-2 text-3xl font-semibold text-amber-700">{money(k.ai_cost, cur)}</p>
                  <p className="mt-1 text-xs text-slate-500">Software, usage, training, implementation, or other AI-related costs.</p>
                </div>
              </div>
            )}

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <EvidenceQualityCard eq={eq} />

              <Card title="What to do next" subtitle="Portfolio decision based on value, evidence and data completeness." testid="decision-card">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-navy/5"><ShieldCheck className="h-4 w-4 text-navy" /></span>
                  <div>
                    <DecisionBadge status={data.decision_status} />
                    <p className="mt-2 text-sm text-slate-600">
                      {view ? `Current net value of ${money(k.current_net_value, cur)} across ${k.entries} AI-assisted ${k.entries === 1 ? "entry" : "entries"}.` : `${k.entries} AI-assisted entries recorded.`}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button className="bg-editorial hover:bg-editorial/90" size="sm" onClick={() => navigate("/signup")} data-testid="demo-decision-cta">
                    <TrendingUp className="mr-1.5 h-3.5 w-3.5" /> Track your own AI ROI
                  </Button>
                </div>
              </Card>
            </div>

            <div className="mt-10 rounded-2xl border border-editorial/20 bg-editorial/[0.04] px-6 py-10 text-center sm:px-10" data-testid="demo-bottom-cta">
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-ink sm:text-[28px]">Ready to see your own AI ROI?</h2>
              <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">
                This is sample data. Create your workspace to track the time AI saves, what it costs, and the return across your business.
              </p>
              <Button size="lg" className="mt-6 bg-editorial hover:bg-editorial/90" onClick={() => navigate("/signup")} data-testid="demo-bottom-signup-btn">
                Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
